import { BuildingUtilityGroup, BuildingUtility, BuildingUtilityBooking, PricingPreview } from '../types';

// Match frontend/src/api/index.ts so LAN / non-localhost deployments hit the correct API host
const resolveApiBaseUrl = (): string => {
  const envBase = process.env.REACT_APP_API_URL;
  let resolvedBase = envBase || '';
  if (
    !resolvedBase ||
    resolvedBase.includes('localhost') ||
    resolvedBase.includes('127.0.0.1')
  ) {
    resolvedBase = `${window.location.protocol}//${window.location.hostname}:3000/api`;
  }
  return resolvedBase;
};

const API_BASE_URL = resolveApiBaseUrl();

// Authentication helpers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

// Utility Groups API
export const buildingUtilityGroupsApi = {
  // Get all utility groups for a building
  getUtilityGroups: async (buildingId: string, page = 1, limit = 10, search = '') => {
    const params = new URLSearchParams({
      buildingId,
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
    });

    const response = await fetch(`${API_BASE_URL}/building-utilities/groups?${params}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch utility groups');
    }

    return response.json();
  },

  // Get a single utility group
  getUtilityGroup: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/groups/${id}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch utility group');
    }

    return response.json();
  },

  // Create a new utility group
  createUtilityGroup: async (data: Partial<BuildingUtilityGroup>) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/groups`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to create utility group');
    }

    return response.json();
  },

  // Create default utility groups for a building
  createDefaultUtilityGroups: async (buildingId: string) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/groups/create-default/${buildingId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to create default utility groups');
    }

    return response.json();
  },

  // Update a utility group
  updateUtilityGroup: async (id: string, data: Partial<BuildingUtilityGroup>) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/groups/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update utility group');
    }

    return response.json();
  },

  // Toggle utility group status
  toggleUtilityGroupStatus: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/groups/${id}/toggle-status`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to toggle utility group status');
    }

    return response.json();
  },

  // Delete a utility group
  deleteUtilityGroup: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/groups/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete utility group');
    }

    return response.json();
  },
};

// Utilities API
export const buildingUtilitiesApi = {
  // Get all utilities for a building/group
  getUtilities: async (buildingId: string, groupId = '', page = 1, limit = 10, search = '') => {
    const params = new URLSearchParams({
      buildingId,
      page: page.toString(),
      limit: limit.toString(),
      ...(groupId && { groupId }),
      ...(search && { search }),
    });

    const response = await fetch(`${API_BASE_URL}/building-utilities/utilities?${params}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch utilities');
    }

    return response.json();
  },

  // Get a single utility
  getUtility: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/utilities/${id}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch utility');
    }

    return response.json();
  },

  // Create a new utility
  createUtility: async (data: Partial<BuildingUtility>) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/utilities`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to create utility');
    }

    return response.json();
  },

  // Update a utility
  updateUtility: async (id: string, data: Partial<BuildingUtility>) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/utilities/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update utility');
    }

    return response.json();
  },

  // Delete a utility
  deleteUtility: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/utilities/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete utility');
    }

    return response.json();
  },

  // Get pricing preview for different slot quantities
  getPricingPreview: async (utilityId: string): Promise<PricingPreview[]> => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/utilities/${utilityId}/pricing-preview`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to get pricing preview');
    }

    return response.json();
  },

  // Calculate booking cost based on number of slots
  calculateBookingCost: async (utilityId: string, numberOfSlots: number) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/utilities/${utilityId}/calculate-cost?slots=${numberOfSlots}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to calculate booking cost');
    }

    return response.json();
  },

  // Check availability for FIXED_SLOTS utility
  checkFixedSlotsAvailability: async (utilityId: string, date: string) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/utilities/${utilityId}/check-slots-availability?date=${date}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to check slots availability');
    }

    return response.json();
  },

  // Check availability for FIXED_SLOTS utility per service hour
  checkFixedSlotsAvailabilityPerServiceHour: async (utilityId: string, date: string, excludeBookingId?: string) => {
    const params = new URLSearchParams({ date });
    if (excludeBookingId) {
      params.append('excludeBookingId', excludeBookingId);
    }
    
    const response = await fetch(`${API_BASE_URL}/building-utilities/utilities/${utilityId}/check-slots-availability-per-hour?${params}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to check slots availability per service hour');
    }

    return response.json();
  },

  // Check availability for TIME_BASED utility
  checkTimeBasedAvailability: async (utilityId: string, date: string) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/utilities/${utilityId}/check-time-availability?date=${date}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to check time availability');
    }

    return response.json();
  },

  // Check availability for NO_BOOKING utility (like library)
  checkNoBookingAvailability: async (utilityId: string, date: string) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/utilities/${utilityId}/check-no-booking-availability?date=${date}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to check no-booking availability');
    }

    return response.json();
  },

  // Check availability for NO_BOOKING utility per service hour
  checkNoBookingAvailabilityPerServiceHour: async (utilityId: string, date: string, excludeBookingId?: string) => {
    const params = new URLSearchParams({ date });
    if (excludeBookingId) {
      params.append('excludeBookingId', excludeBookingId);
    }
    
    const response = await fetch(`${API_BASE_URL}/building-utilities/utilities/${utilityId}/check-no-booking-availability-per-hour?${params}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to check no-booking availability per service hour');
    }

    return response.json();
  },
};

// Bookings API
export const buildingUtilityBookingsApi = {
  // Get all bookings
  getBookings: async (buildingId: string, utilityId = '', apartmentId = '', page = 1, limit = 10, search = '') => {
    const params = new URLSearchParams({
      buildingId,
      page: page.toString(),
      limit: limit.toString(),
      ...(utilityId && { utilityId }),
      ...(apartmentId && { apartmentId }),
      ...(search && { search }),
    });

    const response = await fetch(`${API_BASE_URL}/building-utilities/bookings?${params}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch bookings');
    }

    return response.json();
  },

  // Get a single booking
  getBooking: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/bookings/${id}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch booking');
    }

    return response.json();
  },

  // Create a new booking
  createBooking: async (data: Partial<BuildingUtilityBooking>) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/bookings`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to create booking');
    }

    return response.json();
  },

  // Update a booking
  updateBooking: async (id: string, data: Partial<BuildingUtilityBooking>) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/bookings/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update booking');
    }

    return response.json();
  },

  // Delete a booking
  deleteBooking: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/building-utilities/bookings/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete booking');
    }

    return response.json();
  },
}; 